import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api, { memberVerifyPhotoUrl } from '../utils/api';

export default function VerifyMember() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('Invalid verification link');
      return;
    }
    api.get(`/member-portal/verify/${id}`)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        setError(err.message || 'Verification failed');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const verified = data?.verified === true;
  const member = data?.member;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          {verified ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Member Verified
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This ID card belongs to a verified member.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={
                    member?.profile_picture && id ? memberVerifyPhotoUrl(id) : undefined
                  }
                  imgProps={{
                    onError: (e) => {
                      e.target.style.display = 'none';
                    },
                  }}
                  sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                >
                  {member?.first_name?.[0] || ''}{member?.last_name?.[0] || ''}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {[member?.first_name, member?.last_name].filter(Boolean).join(' ') || 'Member'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {member?.member_id || member?.association_number || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {member?.institution || '—'}
                  </Typography>
                  <Chip
                    label={member?.status || 'Active'}
                    color={member?.status === 'Active' ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'left' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Member benefits include:
                </Typography>
                <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Access to member portal (events, announcements, voting)</li>
                  <li>Digital ID card</li>
                  <li>Member communications</li>
                </Typography>
              </Box>
              <Button
                variant="outlined"
                href="/member-portal"
                sx={{ mt: 2 }}
              >
                Member Portal
              </Button>
            </>
          ) : (
            <>
              <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 1 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Verification Failed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data?.message || error || 'This member could not be verified.'}
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
